/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/sort-comp */
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Form, Input, Message, Segment } from 'semantic-ui-react';
import ClassroomApi from '../../api/ClassroomApi';
import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';
import { ERROR_MESSAGES } from '../../managers/UserManager';
import Modal from '../Modal';

import '../../css/RegisterStudentModal.less';
import UtilityService from '../../services/UtilityService';

const EXISTING_STUDENT_LOOKUP_DELAY_MS = 400;

export default @inject('userManager')
@observer
class RegisterStudentModal extends Component {
  existingStudentLookupTimer = null;

  existingStudentLookupRequestId = 0;

  constructor(props) {
    super(props);

    this.state = {
      passwordInputType: 'password',
      passwordInputType2: 'password',
      accessCode: {
        value: props.accessCode,
        error: null,
        result: null
      },
      firstName: {
        value: '',
        error: null
      },
      lastName: {
        value: '',
        error: null
      },
      email: {
        value: '',
        error: null
      },
      password: {
        value: '',
        error: null
      },
      confirmPassword: {
        value: '',
        error: null
      },
      existingStudent: null,
      addingExistingStudent: false,
      generatingUsername: false,
      generatingPassword: false,
      submitted: false,
      success: false,
      student: false,
      error: null,
      username: ''
    };

    this.ModalBanner = SatCoreComponent('ModalBanner');
    this.ShowPasswordButton = SatCoreComponent('ShowPasswordButton');
  }

  componentWillUnmount() {
    if (this.existingStudentLookupTimer) {
      clearTimeout(this.existingStudentLookupTimer);
    }
  }

  closeRegisterStudentModal = () => {
    this.props.closeRegisterStudentModal();
  }

  handleShowPasswordPressed = (name, isPasswordInput) => {
    const inputType = isPasswordInput ? 'password' : 'text';
    this.setState({ [name]: inputType });
  }

  canAutoGenerateUsername = () => {
    const { firstName, lastName, generatingUsername } = this.state;
    return !generatingUsername && firstName.value.trim().length > 0 && lastName.value.trim().length > 0;
  }

  scheduleExistingStudentLookup = () => {
    if (this.existingStudentLookupTimer) {
      clearTimeout(this.existingStudentLookupTimer);
    }

    this.setState({ existingStudent: null });

    this.existingStudentLookupTimer = setTimeout(() => {
      this.lookupExistingStudent();
    }, EXISTING_STUDENT_LOOKUP_DELAY_MS);
  }

  lookupExistingStudent = async () => {
    const { currentClassroomId, userManager } = this.props;
    const firstName = this.state.firstName.value.trim();
    const lastName = this.state.lastName.value.trim();

    if (!currentClassroomId || !firstName || !lastName) {
      this.setState({ existingStudent: null });
      return;
    }

    const requestId = ++this.existingStudentLookupRequestId;
    const existingStudent = await userManager.viewStudentByFirstAndLastName(
      currentClassroomId,
      firstName,
      lastName
    );

    // Ignore stale responses if the user kept typing
    if (requestId !== this.existingStudentLookupRequestId) {
      return;
    }

    const stillMatches = (
      this.state.firstName.value.trim() === firstName &&
      this.state.lastName.value.trim() === lastName
    );

    this.setState({
      existingStudent: stillMatches ? existingStudent : null
    });
  }

  handleAddExistingStudent = async () => {
    const { currentClassroomId } = this.props;
    const { existingStudent, addingExistingStudent } = this.state;

    if (!existingStudent?.id || !currentClassroomId || addingExistingStudent) {
      return;
    }

    this.setState({ addingExistingStudent: true, error: null });
    try {
      const success = await ClassroomApi.addStudents(currentClassroomId, [existingStudent.id]);
      if (success) {
        this.closeRegisterStudentModal();
      } else {
        this.setState({ addingExistingStudent: false, error: ERROR_MESSAGES.GENERAL });
      }
    } catch (e) {
      console.error(e);
      this.setState({ addingExistingStudent: false, error: ERROR_MESSAGES.GENERAL });
    }
  }

  handleAutoGenerateUsername = async (e) => {
    e.preventDefault();
    if (!this.canAutoGenerateUsername()) {
      return;
    }

    const { userManager } = this.props;
    const { firstName, lastName } = this.state;

    this.setState({ generatingUsername: true, error: null });
    const availableUsername = await userManager.generateUsername(firstName.value.trim(), lastName.value.trim());
    if (availableUsername) {
      this.setState((prevState) => ({
        email: {
          ...prevState.email,
          value: availableUsername,
          error: null
        },
        generatingUsername: false
      }));
    } else {
      this.setState({ generatingUsername: false, error: ERROR_MESSAGES.GENERAL });
    }
  }

  handleAutoGeneratePassword = async (e) => {
    e.preventDefault();
    if (this.state.generatingPassword) {
      return;
    }

    const { userManager } = this.props;

    this.setState({ generatingPassword: true, error: null });
    const tempPassword = await userManager.generatePassword();
    if (tempPassword) {
      this.setState((prevState) => ({
        password: {
          ...prevState.password,
          value: tempPassword,
          error: null
        },
        // Leave confirm password empty so the teacher must cut/paste from the first field
        confirmPassword: {
          ...prevState.confirmPassword,
          value: '',
          error: null
        },
        // Auto-reveal password so the teacher can see and share it with the student
        passwordInputType: 'text',
        generatingPassword: false
      }));
    } else {
      this.setState({ generatingPassword: false, error: ERROR_MESSAGES.GENERAL });
    }
  }

  renderAutoGenerateLink = ({ disabled, onClick, t }) => (
    <a
      className={`auto-generate-link${disabled ? ' disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      role='button'
      tabIndex={disabled ? -1 : 0}>
      {t('autoGenerate', 'Auto-generate')}
    </a>
  )

  renderExistingStudentMessage = (t) => {
    const { existingStudent, addingExistingStudent, firstName, lastName } = this.state;
    if (!existingStudent) {
      return null;
    }

    const message = t('existingStudentMessage', {
      defaultValue: '{{firstName}} {{lastName}} ( {{username}} ) already exists in the system. Is this the student you would like to add?',
      firstName: firstName.value.trim() || existingStudent.firstName,
      lastName: lastName.value.trim() || existingStudent.lastName,
      username: existingStudent.username
    });

    return (
      <Message className='existing-student-message' error visible>
        <p>{message}</p>
        <Button
          basic
          className='add-existing-student-button'
          disabled={addingExistingStudent}
          loading={addingExistingStudent}
          onClick={this.handleAddExistingStudent}
          type='button'>
          {t('addStudent', 'Add Student')}
        </Button>
      </Message>
    );
  }

  render() {
    const { registerStudentOpen, labelInfo, contentName, t } = this.props;
    const { ModalBanner } = this;
    const { ShowPasswordButton } = this;
    const canGenerateUsername = this.canAutoGenerateUsername();

    const tooltipAddNewStudentModalTitleLocation = t('tooltipAddNewStudentModalTitleLocation');

    const infoPopupText = tooltipAddNewStudentModalTitleLocation ? (
      UtilityService.reactHtmlParserWrapper(tooltipAddNewStudentModalTitleLocation)?.parsed
    ) : '';

    const headerTitle = contentName || (labelInfo !== undefined && labelInfo !== null && labelInfo !== '') ? labelInfo : t('addNewStudent');

    return (
      <>
        <Modal
          className='RegisterStudentModal'
          closeOnDimmerClick={false}
          closeOnEscape={true}
          onClose={this.closeRegisterStudentModal}
          open={registerStudentOpen}
          size='tiny'>
          <ModalBanner
            infoPopupText={infoPopupText}
            // label={label}
            onClose={this.closeRegisterStudentModal}
            title={headerTitle} />
          <Modal.Content>
            <Form>
              <Segment attached className='element-body-register'>
                <Form.Field>
                  <Form.Input
                    label={t('accessCode', 'Access Code')}
                    name='accessCode'
                    onChange={this.handleChange}
                    onFocus={this.clearError}
                    placeholder={t('accessCodePlaceholder', 'Enter access code')}
                    readOnly={true}
                    type='text'
                    value={this.state.accessCode.value} />
                  <Message
                    content={this.state.accessCode.error}
                    error
                    visible={this.state.accessCode.error !== null} />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label='Student First Name'
                    name='firstName'
                    onChange={this.handleChange}
                    onFocus={this.clearError}
                    placeholder='Student First Name'
                    type='text'
                    value={this.state.firstName.value} />
                </Form.Field>

                <Form.Field>
                  <Form.Input
                    label='Student Last Name'
                    name='lastName'
                    onChange={this.handleChange}
                    onFocus={this.clearError}
                    placeholder='Student Last Name'
                    type='text'
                    value={this.state.lastName.value} />
                </Form.Field>

                {this.renderExistingStudentMessage(t)}

                <Form.Field>
                  <div className='field-label-row'>
                    <label>{t('emailLabel', 'Student Username/ email')}</label>
                    {this.renderAutoGenerateLink({
                      disabled: !canGenerateUsername,
                      onClick: this.handleAutoGenerateUsername,
                      t
                    })}
                  </div>
                  <Input
                    name='email'
                    onChange={this.handleChange}
                    onFocus={this.clearError}
                    placeholder={t('emailLabel', 'Student Username/ email')}
                    type='text'
                    value={this.state.email.value} />

                  <Message
                    content={this.state.email.error}
                    error
                    visible={this.state.email.error !== null} />
                </Form.Field>
                <Form.Field>
                  <div className='field-label-row'>
                    <label>Enter Student Password</label>
                    {this.renderAutoGenerateLink({
                      disabled: this.state.generatingPassword,
                      onClick: this.handleAutoGeneratePassword,
                      t
                    })}
                  </div>
                  <Input
                    autoComplete='current-password'
                    label={(
                      <ShowPasswordButton
                        isPassword={(this.state.passwordInputType === 'password')}
                        name='passwordInputType'
                        onFlip={this.handleShowPasswordPressed} />
                    )}
                    labelPosition='right'
                    name='password'
                    onChange={this.handleChange}
                    onFocus={this.clearError}
                    onKeyPress={(e) => e.stopPropagation()}
                    placeholder='Enter Student Password'
                    type={this.state.passwordInputType}
                    value={this.state.password.value} />
                </Form.Field>

                <Form.Field>
                  <label>Confirm Student Password</label>
                  <Input
                    autoComplete='current-password'
                    label={(
                      <ShowPasswordButton
                        isPassword={(this.state.passwordInputType2 === 'password')}
                        name='passwordInputType2'
                        onFlip={this.handleShowPasswordPressed} />
                    )}
                    labelPosition='right'
                    name='confirmPassword'
                    onChange={this.handleChange}
                    onFocus={this.clearError}
                    onKeyPress={(e) => e.stopPropagation()}
                    placeholder='Confirm Student Password'
                    type={this.state.passwordInputType2}
                    value={this.state.confirmPassword.value} />
                  <Message
                    content={this.state.confirmPassword.error}
                    error
                    visible={this.state.confirmPassword.error !== null} />
                  <Message
                    content={this.state.password.error}
                    error
                    visible={this.state.password.error !== null} />
                </Form.Field>

                <Form.Field />
              </Segment>
              <Message
                content={this.state.error}
                error
                visible={this.state.error !== null} />
            </Form>
          </Modal.Content>

          <Modal.Actions>
            <Button basic className='cancelButton' onClick={this.closeRegisterStudentModal} primary>Cancel</Button>
            <Button disabled={this.state.submitted || !this.isComplete()} onClick={this.handleFormSubmit} primary>Save</Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => {
      const newState = { ...prevState };
      newState[name].value = value;
      return newState;
    }, () => {
      if (name === 'firstName' || name === 'lastName') {
        this.scheduleExistingStudentLookup();
      }
    });
  }

  clearError = (e) => {
    const { name } = e.target;
    this.setState((prevState) => {
      const newState = { ...prevState };
      newState[name].error = null;
      return newState;
    });
  }

  validate = async () => {
    const { userManager, t } = this.props;
    let valid = true;
    const accessCode = { ...this.state.accessCode };
    await userManager.validateAccessCode(this.state.accessCode);
    const { accessCodeResult } = userManager;

    accessCode.result = accessCodeResult;
    if (accessCodeResult.codeValid && (accessCodeResult.codeType === 'classroom' || accessCodeResult.codeType === 'school')) {
      // classroom access code type can be "classroom" for co-teacher or student so have to check the category now too.
      this.setState({
        student: accessCodeResult.codeType === 'classroom' && accessCodeResult.codeCategory === 'classroom'
      });
      accessCode.error = null;
    } else {
      accessCode.error = ERROR_MESSAGES.ACCESS_CODE_INVALID;
    }
    this.setState({ accessCode });
    if (accessCode.error !== null) {
      valid = false;
    }

    // check username/email for format and existence in the DB
    if (this.state.email.value) {
      const email = { ...this.state.email };
      // check if we have a valid username/email format
      const usernameResult = userManager.validateStudentUsernameFormat(email.value);
      email.error = null;
      if (!usernameResult.valid) {
          email.error = usernameResult.errorMessage;
      } else {
        // if format is valid, check if the username exists
        await userManager.validateUser(this.state.accessCode, this.state.email);
        const { emailResult } = userManager;
        if (emailResult.emailAddressExists) {
          if (userManager.allowStudentUsernamesAsEmail) {
            email.error = t('userExistsError', ERROR_MESSAGES.USER_EXISTS);
          } else {
            email.error = t('userExistsErrorNoEmail', ERROR_MESSAGES.USER_EXISTS_NO_EMAIL);
          }
        } else {
          email.error = null;
        }
      }
      this.setState({ email });
      if (email.error !== null) {
        valid = false;
      }
    }

    const { password, confirmPassword } = this.state;
    if (password.value !== confirmPassword.value) {
      confirmPassword.error = ERROR_MESSAGES.PASSWORDS_DO_NOT_MATCH;
      valid = false;
    } else {
      confirmPassword.error = null;
    }
    if (password.value !== null && password.value.length > 0 && password.value.length < 8) {
      password.error = ERROR_MESSAGES.PASSWORD_LENGTH;
      valid = false;
    } else {
      password.error = null;
    }
    this.setState({ password, confirmPassword });

    return valid;
  }

  isComplete = () => {
    const { accessCode, firstName, lastName, email, password, confirmPassword } = this.state;
    return !!(
      accessCode.value.length && !accessCode.error &&
      firstName.value.length && !firstName.error &&
      lastName.value.length && !lastName.error &&
      email.value.length && !email.error &&
      password.value.length && !password.error &&
      confirmPassword.value.length && !confirmPassword.error &&
      password.value === confirmPassword.value
    );
  }

  handleFormSubmit = async (e) => {
    const { userManager } = this.props;
    e.preventDefault();
    const valid = await this.validate();
    if (!valid) {
      return;
    }
    this.setState({ submitted: true, error: null });
    const accessCode = this.state.accessCode.result;

    const body = {
      signUpCode: accessCode.signupCode,
      institutionId: accessCode.institutionId,
      password: this.state.password.value,
      firstName: this.state.firstName.value,
      lastName: this.state.lastName.value,
      publisherSatelliteCode: this.publisherSatelliteCode,
      username: this.state.email.value
    };

    const result = await userManager.registerUser(body, accessCode);
    if (result) {
      this.closeRegisterStudentModal();
    } else {
      this.setState({ submitted: false, error: ERROR_MESSAGES.GENERAL });
    }
  }
}

SatCoreRegister('RegisterStudentModal', RegisterStudentModal);
